import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { Observable, BehaviorSubject, Observer, ReplaySubject, Subscription } from 'rxjs';
import { take, shareReplay } from 'rxjs/operators';

import { CacheProgress, GroupStateMap, ResourceState, GroupState, ObservableValue } from '../models';
import { MapService, MoteService, FetchService, ProjectService, IconService } from '../providers';
// For now disable caching offline content in index DB - use regular caches (with less storage space on iOS)
// import { ServiceWorkerService } from '../service-worker/service-worker.service';
// import { caches } from '../models/idx-cache';
import { ProgressManager } from '../models/progress-manager';
import { OfflineResources, SaveResourceService } from "./offline-resources";

@Injectable({ providedIn: 'root' })
export class OfflineService implements SaveResourceService {
    public static OFFLINE_CONTENT_META = "MotesMeta";

    private downloadMachine: ProgressManager = null;

    public offlineEnabled = false;
    public cachingDone = false;

    private initSubject = new ReplaySubject<boolean>(1);
    private offlineStateSubject = new BehaviorSubject<boolean>(this.offlineEnabled);

    // Helper class generating list of offline resources needed to be downloaded
    private offlineResources: OfflineResources;

    private progressSub: Subscription = null;
    private errorSub: Subscription = null;

    constructor(
        moteService: MoteService,
        mapService: MapService,
        fetchService: FetchService,
        iconService: IconService,
        private projectService: ProjectService,
        private sanitizer: DomSanitizer) {
        // Init offline resource helper instance
        this.offlineResources = new OfflineResources(fetchService, projectService, moteService, mapService, iconService, this);
    }

    /**
    * Initialize offline service
    * @return True if offline mode is enabled or false in case it is disabled
    */
    public init(projectKey: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            // Return immediately if already initialised
            if(this.downloadMachine) return resolve(this.offlineEnabled);

            // Create a new progress machine
            this.downloadMachine = new ProgressManager();

            // Await cache map state to load
            let isOfflineEnabled = await this.initState(projectKey);

            // Watch download progress
            this.progressSub = this.watchDownloadProgress().subscribe(async ev => {
                // Save final state in cache
                if(ev.totalPct >= 100) {
                    console.log("Download done, saving resource map ", ev);
                    await this.saveCacheMap(this.downloadMachine.getResourceMap());
                }
            }, err => {
                console.log("Download/save progress error ", err);
            });

            this.downloadMachine.init();
            this.initSubject.next(true);
            resolve(isOfflineEnabled);
        });
    }

    /**
    * Wait for offline service to initialize
    */
    public awaitInit(): Promise<boolean> {
        return this.initSubject.pipe(take(1)).toPromise();
    }

    /**
    * Get offline cache map
    */
    public getResourceMap(): GroupStateMap {
        return this.downloadMachine.getResourceMap();
    }

    /** 
    * Check if the project is currently available offline in users cache
    */
    public isProjectAvailableOffline(projectKey: string): boolean {
        let cacheMap = this.getResourceMap();
        console.log("Cache map ", cacheMap);

        let state = cacheMap[projectKey];
        return state ? state.status == 'Cached' : false;
    }

    /**
    * Watch state of cached projects
    */
    public watchCachingMap(): Observable<GroupStateMap> {
        return this.downloadMachine.watchResourceMap();
    }

    /**
    * Watch offline enabled state
    */
    public watchOfflineState(): Observable<boolean> {
        return this.offlineStateSubject.asObservable().pipe(shareReplay(1));
    }

    /**
    *  Watch download/caching progress
    */
    public watchDownloadProgress(): Observable<CacheProgress> {
        return this.downloadMachine.watchCachingProgress();
    }

    /**
    *  Watch download/caching progress
    */
    public watchDownloadErrors(): Observable<ResourceState> {
        return this.downloadMachine.watchResourceErrors();
    }

    /**
    * Cache a world and all its media resources for offline usage.
    * @projectKey The key of the project to be cached for offline usage
    * @enable Enable (download) or disable (remove) the offline cache of the specified project
    */
    public enableOfflineCache(projectKey: string, enable: boolean): Promise<boolean> {
        // Promise to be resolved when caching process has started
        return new Promise(async (resolve) => {
            // Delete project cache
            if(!enable) return this.deleteProjectCache(projectKey);

            // Disable offline mode in order to download resources
            await this.enableOfflineMode(false);
            // Do not await service worker - just expect it to be theres as otherwise we need to have a reference to service worker
            // await this.serviceWorker.awaitActive().catch(err => console.log("Service worker is missing (are we in debug?), continue anyway..."));

            // Init resource group and add resources
            let resourceGroup = this.downloadMachine.initResourceGroup(projectKey);
            resourceGroup.resourceList = await this.genResourceList(projectKey, resourceGroup.abortCtrl);
            resourceGroup.status = 'Downloading';

            // Add resources to download queue and notify listners
            this.downloadMachine.addResources(resourceGroup.resourceList);
            this.downloadMachine.updateResourceGroup(projectKey, resourceGroup);
            resolve(true);
        });
    }

    /**
     * Generate a list of resource requests needed for project to work offline 
     */
    private genResourceList(projectKey: string, abortCtrl: AbortController): Promise<ObservableValue<ResourceState>[]> {
        // Promise to be resolved when caching process has started
        return this.offlineResources.genResourceList(projectKey, abortCtrl);
    }

    public async resumeDownload(projectKey: string, missingResources: ResourceState[]) {
        // Disable offline mode in order to download resources
        await this.enableOfflineMode(false);
        // Do not await service worker - just expect it to be theres as otherwise we need to have a reference to service worker
        // await this.serviceWorker.awaitActive().catch(err => console.log("Service worker is missing (are we in debug?), continue anyway..."));

        // Get resource list and init new abort controller
        let resourceGroup = this.downloadMachine.getResourceGroup(projectKey);
        resourceGroup.abortCtrl = new AbortController();

        // Get list of all resource observable in this project
        let fullResourceList = await this.genResourceList(projectKey, resourceGroup.abortCtrl);

        // Find the missing resources and add to resource list
        resourceGroup.resourceList = fullResourceList.filter((resource) => {
            return missingResources.find(missing => resource.lastVal.url == missing.url);
        });
        resourceGroup.status = 'Downloading';

        // Add resources to download queue and notify listners
        this.downloadMachine.addResources(resourceGroup.resourceList);
        this.downloadMachine.updateResourceGroup(projectKey, resourceGroup);
    }

    /**
    * Enable offline mode by setting this state.  This state is observed by the serviceworker,
    * which when enabled till serve offline responses only
    */
    public async enableOfflineMode(enable: boolean): Promise<boolean> {
        // Avoid referencing caches in SSR (prerender)
        if(typeof caches == 'undefined') return false;

        return new Promise(async resolve => {
            // Offline mode hysteresis
            if(this.offlineEnabled == enable) return resolve(true);
            console.log("Enable offline service mode ", enable);
            // Set offline state
            this.offlineEnabled = enable;
            // Save state in cache
            let cache = await caches.open(OfflineService.OFFLINE_CONTENT_META);
            let state = { 'offlineMode': enable, 'projectKey': this.projectService.activeProject?.key };

            // Save cache state in cache as a response
            let res = await cache.put("/motes_app_state.json", new Response(JSON.stringify(state))).catch((err) => {
                console.warn("Could not save offline cache state ", err);
                return false;
            });
            // Notify listners
            this.offlineStateSubject.next(enable);
            resolve(res ? true : false);
        });
    }

    private initState(urlProject: string): Promise<boolean> {
        return new Promise(async (resolve) => {
            // Get offline state from cache
            let [offlineEnabled, storedProject] = await this.loadStoredOfflineMode();
            this.offlineEnabled = offlineEnabled;
            // Load stored cache map
            this.downloadMachine.restoreResourceMap(await this.loadCacheMap());

            // Use project from url or fall back to stored project key
            let projectKey = urlProject || storedProject;

            // Init offline state according to active project state
            let projectCache = this.downloadMachine.getResourceGroup(projectKey);

            // If in offline mode and active project cache has been deleted
            if(offlineEnabled && (!projectCache || projectCache.status != 'Cached')) {
                // Force disable offline mode
                console.warn("Force online mode - cache of active has been deleted?", this.downloadMachine, projectCache, projectKey);
                await this.enableOfflineMode(false);
            }

            // Notify listners
            // console.log("Init offline service mode ", this.offlineEnabled);
            this.offlineStateSubject.next(this.offlineEnabled);
            resolve(offlineEnabled);
        });
    }

    private async loadStoredOfflineMode(): Promise<[boolean, string]> {
        // Avoid referencing caches in SSR (prerender)
        if(typeof caches == 'undefined') return [false, ""];

        // Get offline mode from persisted storage
        let cache = await caches.open(OfflineService.OFFLINE_CONTENT_META);
        let state = await cache.match("/motes_app_state.json");
        if(!state) return [false, ""];
        let props = await state.json();
        return [props.offlineMode, props.projectKey];
    }

    private deleteProjectCache(projectKey: string): Promise<boolean> {
        let resourceGroup = this.downloadMachine.getResourceGroup(projectKey);
        if(!resourceGroup) resourceGroup = this.downloadMachine.initResourceGroup(projectKey);

        // Cacel all resource downloads
        for(let resource of resourceGroup.resourceList) {
            // Delete resource from cache if already cached
            if(resource.lastVal.status == 'Cached') {
                resource.lastVal.status = 'Cancled';
                //deletedBytes += resource.lastVal.bytesTotal;
                this.removeResource(resource.lastVal.url);
            }
        }
        this.downloadMachine.cancelResourceGroup(projectKey);
        return this.saveCacheMap(this.downloadMachine.getResourceMap());
    }

    /**
    * Delete a resource blob from the cache (indexDB)
    */
    public removeResource(req: string, cacheName: string = "MediaStore"): Promise<boolean> {
        return new Promise(async resolve => {
            // Delete resource cache entry from all motesMedia caches
            let cache = await caches.open(cacheName);
            await cache.delete(req).catch(err => console.info("Could not delete cache: " + req));
            resolve(true);
        });
    }

    /**
    * Save a resource blob in the cache (indexDB)
    */
    public saveResource(req: string, rsp: Response, cacheName: string = "MediaStore"): Promise<boolean> {
        return new Promise(async (resolve, reject) => {
            // Open cache aka. poly filled cache storage (indexDB implemented in idx-cache.ts) 
            let cache = await caches.open(cacheName).catch(() => null);
            if(!cache) return reject();

            // Save response in cache
            cache.put(req, rsp).then((res) => {
                // Just make sure these are not taking memory when returning (GC will kill them)
                delete rsp.arrayBuffer;
                delete rsp.blob;
                resolve(true);
            }).catch((err: DOMException) => {
                // If we get a quota error we cant do no more, throw error
                if(err && err.name == 'QuotaExceededError') {
                    console.warn("Quota exceeded error from indexDB, could not save resource");
                    this.downloadMachine.reportError("quota_error", "Sorry, there is not enough space on this device to enable offline mode");
                }
                else {
                    console.warn("Could not save resource for some reason, ignoring: ", err);
                    this.downloadMachine.reportError(err ? err.name : "No error", "Could not enable offline mode for some reason, sorry: " + err.message);
                }
                resolve(false);
            });
        });
    }

    /**
    * Load a request response from index DB
    */
    public loadResource(req: string, cacheName: string = "MediaStore"): Promise<Response | null> {
        return new Promise(async resolve => {
            let cache = await caches.open(cacheName);
            let stored = await cache.match(req).catch(err => null);
            resolve(stored);
        });
    }

    /**
    * Load request response from index DB and generate a local media url
    * OBS: The returned url must be disposed using deleteBlobUrl()
    */
    public async loadResourceUrl(req: string, originFallback: boolean = false, cacheName: string = "MediaStore"): Promise<{ blob?: string, url: string }> {
        // Never resolve offline urls if we are not in offline mode
        if(!this.offlineEnabled) return Promise.resolve({ url: req });
        // Never load resource twice if it was already offline enabled
        if(typeof req != 'string' || req.startsWith('unsafe:') || req.startsWith('blob:')) return Promise.resolve({ url: req });

        // Load resource from cache
        let rsp = await this.loadResource(req, cacheName);
        // Just return a empty string if the requested resource is not offline stored
        // if(!rsp) console.warn("Fall back to origin offline image ", req, cacheName);
        let fallbackUrl = originFallback ? req : "";
        if(!rsp) return Promise.resolve({ url: fallbackUrl, safeUrl: fallbackUrl });
        // Get blob content of response
        let blob = await rsp.blob();
        let blobUrl = this.generateBlobUrl(blob);
        let safeUrl = this.sanitizer.bypassSecurityTrustUrl(blobUrl) as string
        return Promise.resolve({ blob: blobUrl, url: safeUrl });
    }

    /**
    * Generate a local media url from blob object
    */
    public generateBlobUrl(file: Blob): string {
        if(!file) return "";
        let URL = window.URL || (window as any).webkitURL;
        return URL.createObjectURL(file);
    }

    /**
    * Clean up a blob url resource
    */
    public deleteBlobUrl(url: string): void {
        if(!url) return;
        let URL = window.URL || (window as any).webkitURL;
        URL.revokeObjectURL(url);
    }

    private saveCacheMap(cacheState: GroupStateMap): Promise<boolean> {
        return new Promise(async resolve => {

            const cacheMapCopy = Object.assign({}, cacheState);
            let cleanCacheMap = Object.keys(cacheMapCopy).reduce((obj, key) => {
                obj[key] = this.cacheMapFilter(cacheMapCopy[key]);
                return obj;
            }, {});

            let cache = await caches.open(OfflineService.OFFLINE_CONTENT_META);
            let cacheContainer = new Response(JSON.stringify(cleanCacheMap));
            await cache.put("/cache_map.json", cacheContainer || null).catch(err => console.warn("Could not save cache map ", err));
            resolve(true);
        });
    }

    private cacheMapFilter(obj: GroupState) {
        return {
            status: obj.status,
            totalBytes: obj.totalBytes,
            resourceList: obj.resourceList,
        };
    }

    private async loadCacheMap(): Promise<GroupStateMap> {
        // Avoid referencing caches in SSR (prerender)
        if(typeof caches == 'undefined') return {};

        let cache = await caches.open(OfflineService.OFFLINE_CONTENT_META);
        let stored = await cache.match("/cache_map.json");
        // Init empty map if none stored yet
        if(!stored) return {};
        let cacheMap = await stored.json();
        // Restore abort controller
        Object.keys(cacheMap).forEach(key => cacheMap[key].abortCtrl = new AbortController());
        return cacheMap;
    }

    public destroy() {
        this.downloadMachine.destroy();
        if(this.progressSub) this.progressSub.unsubscribe();
    }
}
